import type { Band } from "../game/rules.ts";

/** The app's sounds (`FeedbackSounds` and `ToneSynth` in the iOS app), made the same way so they match. */
export type SoundEvent = { kind: "pick" } | { kind: "guess"; band: Band; newClue: boolean } | { kind: "won" } | { kind: "lost" };

export interface Note {
  /** Hertz; 0 is a rest. */
  frequency: number;
  /** Its slot: the next note starts after this. */
  seconds: number;
  gain?: number;
  /** How long it keeps sounding past its slot, under the notes after it, like a bell. */
  ring?: number;
  attack?: number;
  /** A soft overtone an octave up, for a warmer, bell-like sound. */
  warmth?: number;
}

const PITCH: Record<Band, number> = { far: 330, mild: 392, warm: 440, hot: 523.25, nextDoor: 659.25, correct: 783.99 };

export function notes(e: SoundEvent): Note[] {
  switch (e.kind) {
    case "pick": return [{ frequency: 1600, seconds: 0.03, gain: 0.15 }];
    case "guess": {
      const tone: Note[] = [{ frequency: PITCH[e.band], seconds: 0.16, gain: 0.3 }];
      const clue: Note[] = [{ frequency: 0, seconds: 0.08 }, { frequency: 784, seconds: 0.08, gain: 0.22 }, { frequency: 988, seconds: 0.12, gain: 0.22 }];
      return e.newClue ? [...tone, ...clue] : tone;
    }
    case "won": {
      // C, E, G struck in turn and left to ring together, fading out like a small bell.
      const bell = (frequency: number, ring: number): Note => ({ frequency, seconds: 0.09, gain: 0.2, ring, attack: 0.02, warmth: 0.25 });
      return [bell(523.25, 0.5), bell(659.25, 0.5), bell(783.99, 0.45)];
    }
    case "lost": return [{ frequency: 233.08, seconds: 0.18, gain: 0.25 }, { frequency: 196, seconds: 0.32, gain: 0.25 }];
  }
}

/** Sine notes with a quick attack and a smooth decay, mixed into one buffer of samples. */
export function synth(list: Note[], sampleRate: number): Float32Array<ArrayBuffer> {
  const starts: number[] = [];
  let position = 0;
  for (const n of list) { starts.push(position); position += Math.floor(n.seconds * sampleRate); }
  const total = Math.max(0, ...list.map((n, i) => starts[i]! + Math.floor((n.seconds + (n.ring ?? 0)) * sampleRate)));
  const mix = new Float32Array(total);
  list.forEach((n, index) => {
    if (n.frequency <= 0) return;
    const gain = n.gain ?? 0.3, warmth = n.warmth ?? 0;
    const count = Math.floor((n.seconds + (n.ring ?? 0)) * sampleRate);
    const attack = Math.max(1, (n.attack ?? 0.005) * sampleRate);
    // A last fade so nothing clicks: 30 ms, or a quarter of a very short note.
    const release = Math.max(1, Math.min(count / 4, 0.03 * sampleRate));
    for (let i = 0; i < count; i++) {
      const t = i / sampleRate;
      const envelope = Math.min(1, i / attack) * Math.min(1, (count - i) / release) * Math.exp((-4 * i) / count);
      const tone = Math.sin(2 * Math.PI * n.frequency * t) + warmth * Math.sin(4 * Math.PI * n.frequency * t);
      mix[starts[index]! + i]! += (tone / (1 + warmth)) * gain * envelope;
    }
  });
  for (let i = 0; i < mix.length; i++) mix[i] = Math.max(-1, Math.min(1, mix[i]!));
  return mix;
}

let context: AudioContext | undefined;
const buffers = new Map<string, AudioBuffer>();

/** Plays an event's sound. Made on first use, inside the tap or key press that browsers require. Never throws. */
export function playSound(e: SoundEvent): void {
  try {
    context ??= new AudioContext();
    if (context.state === "suspended") void context.resume();
    const key = JSON.stringify(e);
    let buffer = buffers.get(key);
    if (!buffer) {
      const samples = synth(notes(e), context.sampleRate);
      buffer = context.createBuffer(1, Math.max(1, samples.length), context.sampleRate);
      buffer.copyToChannel(samples, 0);
      buffers.set(key, buffer);
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.start();
  } catch {
    // No Web Audio: the game is the same without sound.
  }
}
